import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck, BookOpen, FileText, GraduationCap,
  Clock, Library, BarChart3, Bot, TrendingUp, TrendingDown,
  Users, Building2, AlertCircle, CheckCircle, type LucideIcon,
} from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { apiGet } from '../lib/auth';
import type { AuthUser } from '../lib/auth';

// ── Colour tokens (inline to avoid import cycle) ──────────────────────────────

const C = {
  yellow:     '#F7D000',
  yellowLight:'#FFF8D6',
  black:      '#1A1A1A',
  white:      '#FFFFFF',
  canvas:     '#F9FAFB',
  border:     '#E5E7EB',
  gray50:     '#F9FAFB',
  gray100:    '#F3F4F6',
  gray400:    '#9CA3AF',
  gray500:    '#6B7280',
  gray700:    '#374151',
  gray900:    '#111827',
  success:    '#2E7D32',
  successBg:  '#DCFCE7',
  successText:'#166534',
  error:      '#DC2626',
  errorBg:    '#FEE2E2',
  errorText:  '#991B1B',
  warning:    '#D97706',
  warningBg:  '#FEF3C7',
  warningText:'#92400E',
  info:       '#1D4ED8',
  infoBg:     '#EFF6FF',
  infoText:   '#1E40AF',
};

const cardShadow = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)';
const cardShadowHover = '0 4px 12px rgba(0,0,0,0.10)';

// ── Types ─────────────────────────────────────────────────────────────────────

interface StatCardData {
  labelMs:  string;
  labelEn:  string;
  value:    string | number;
  trend?:   { dir: 'up' | 'down'; pct: string };
  color?:   string;
  Icon:     LucideIcon;
}

interface QuickAction {
  labelMs: string;
  labelEn: string;
  descMs:  string;
  descEn:  string;
  path:    string;
  Icon:    LucideIcon;
  color:   string;
}

// ── Shared card component ─────────────────────────────────────────────────────

function Card({
  children,
  style,
  onClick,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: C.white,
        borderRadius:    '12px',
        border:          `1px solid ${C.border}`,
        padding:         '24px',
        boxShadow:       hovered && onClick ? cardShadowHover : cardShadow,
        transition:      'box-shadow 0.15s, transform 0.15s',
        transform:       hovered && onClick ? 'translateY(-1px)' : 'none',
        cursor:          onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ data, lang }: { data: StatCardData; lang: string }) {
  const label = lang === 'en' ? data.labelEn : data.labelMs;
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: '13px', color: C.gray500, marginBottom: '8px', fontWeight: 500 }}>
            {label}
          </p>
          <p style={{ fontSize: '32px', fontWeight: 700, color: C.gray900, lineHeight: 1, marginBottom: '8px' }}>
            {typeof data.value === 'number' ? data.value.toLocaleString('ms-BN') : data.value}
          </p>
          {data.trend && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {data.trend.dir === 'up'
                ? <TrendingUp size={14} color={C.success} />
                : <TrendingDown size={14} color={C.error} />
              }
              <span style={{ fontSize: '12px', color: data.trend.dir === 'up' ? C.success : C.error, fontWeight: 600 }}>
                {data.trend.pct}
              </span>
              <span style={{ fontSize: '12px', color: C.gray400 }}>
                {lang === 'en' ? 'vs last week' : 'berbanding minggu lalu'}
              </span>
            </div>
          )}
        </div>
        <div
          style={{
            width:           '44px',
            height:          '44px',
            borderRadius:    '10px',
            backgroundColor: data.color ? `${data.color}20` : C.yellowLight,
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            flexShrink:      0,
          }}
        >
          <data.Icon size={22} strokeWidth={1.8} color={data.color ?? C.black} />
        </div>
      </div>
    </Card>
  );
}

// ── Mini sparkline (SVG) ──────────────────────────────────────────────────────

function Sparkline({ values, color = C.yellow }: { values: number[]; color?: string }) {
  if (values.length < 2) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const w = 120; const h = 40;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts.join(' ')}
      />
    </svg>
  );
}

// ── Mini bar chart ────────────────────────────────────────────────────────────

function MiniBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '13px', color: C.gray700 }}>{label}</span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: C.gray900 }}>
          {value.toLocaleString('ms-BN')}
        </span>
      </div>
      <div style={{ height: '6px', backgroundColor: C.gray100, borderRadius: '999px', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%', width: `${pct}%`,
            backgroundColor: color, borderRadius: '999px',
            transition: 'width 0.4s ease',
          }}
        />
      </div>
    </div>
  );
}

// ── Attendance heatmap row ────────────────────────────────────────────────────

function AttendanceRow({ label, present, total }: { label: string; present: number; total: number }) {
  const pct = total > 0 ? Math.round((present / total) * 100) : 0;
  const color = pct >= 90 ? C.success : pct >= 75 ? C.warning : C.error;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: '13px', color: C.gray700, flex: 1 }}>{label}</span>
      <div style={{ width: '80px', height: '6px', backgroundColor: C.gray100, borderRadius: '999px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: '999px' }} />
      </div>
      <span style={{ fontSize: '13px', fontWeight: 600, color, width: '40px', textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

// ── Quick action card ─────────────────────────────────────────────────────────

function QuickActionCard({ action, lang, onClick }: { action: QuickAction; lang: string; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: C.white,
        borderRadius:    '12px',
        border:          `1px solid ${hovered ? action.color : C.border}`,
        padding:         '20px',
        cursor:          'pointer',
        transition:      'all 0.15s',
        boxShadow:       hovered ? cardShadowHover : cardShadow,
        transform:       hovered ? 'translateY(-2px)' : 'none',
      }}
    >
      <div
        style={{
          width:  '40px', height: '40px',
          borderRadius: '10px',
          backgroundColor: `${action.color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '12px',
        }}
      >
        <action.Icon size={20} color={action.color} strokeWidth={1.8} />
      </div>
      <p style={{ fontSize: '14px', fontWeight: 700, color: C.gray900, marginBottom: '4px' }}>
        {lang === 'en' ? action.labelEn : action.labelMs}
      </p>
      <p style={{ fontSize: '12px', color: C.gray500, lineHeight: 1.4 }}>
        {lang === 'en' ? action.descEn : action.descMs}
      </p>
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────

function SectionHeader({ ms, en, lang }: { ms: string; en: string; lang: string }) {
  return (
    <h2 style={{
      fontSize: '16px', fontWeight: 700, color: C.gray900,
      marginBottom: '16px', marginTop: '32px',
      fontFamily: '"Noto Sans", sans-serif',
      paddingBottom: '8px',
      borderBottom: `2px solid ${C.border}`,
    }}>
      {lang === 'en' ? en : ms}
    </h2>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────

function Skeleton({ width = '100%', height = '20px', style }: { width?: string; height?: string; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        width, height,
        borderRadius: '6px',
        background: `linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)`,
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.4s infinite',
        ...style,
      }}
    />
  );
}

// ── MOE Admin Dashboard ───────────────────────────────────────────────────────

function MOEDashboard({ lang }: { lang: string }) {
  const [loading, setLoading] = useState(true);

  const stats: StatCardData[] = [
    { labelMs: 'Jumlah Sekolah',  labelEn: 'Total Schools',    value: 200, Icon: Building2,    color: C.info },
    { labelMs: 'Jumlah Pelajar',  labelEn: 'Total Students',   value: 52840, trend: { dir: 'up', pct: '+2.3%' }, Icon: Users,      color: C.success },
    { labelMs: 'Jumlah Pendidik', labelEn: 'Total Educators',  value: 5120, Icon: GraduationCap, color: C.warning },
    { labelMs: 'Kehadiran Hari Ini', labelEn: 'Today\'s Attendance', value: '91.4%', trend: { dir: 'up', pct: '+0.8%' }, Icon: CalendarCheck, color: C.success },
  ];

  const districtData = [
    { label: lang === 'en' ? 'Brunei-Muara' : 'Brunei-Muara', value: 22480, color: C.info },
    { label: lang === 'en' ? 'Tutong' : 'Tutong',             value: 11240, color: C.success },
    { label: lang === 'en' ? 'Belait' : 'Belait',             value: 13680, color: C.warning },
    { label: lang === 'en' ? 'Temburong' : 'Temburong',       value: 5440,  color: '#8B5CF6' },
  ];
  const districtTotal = districtData.reduce((s, d) => s + d.value, 0);

  const attendanceTrend = [88, 90, 87, 91, 89, 93, 91, 92, 90, 91, 93, 91, 94, 91];

  useEffect(() => { setTimeout(() => setLoading(false), 600); }, []);

  if (loading) {
    return (
      <>
        <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px', marginBottom: '32px' }}>
          {[1,2,3,4].map(i => <Card key={i}><Skeleton height="80px" /></Card>)}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '0' }}>
        {stats.map((s) => <StatCard key={s.labelEn} data={s} lang={lang} />)}
      </div>

      <SectionHeader ms="Analitik Nasional" en="National Analytics" lang={lang} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Attendance trend */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <p style={{ fontSize: '14px', fontWeight: 700, color: C.gray900, marginBottom: '2px' }}>
                {lang === 'en' ? 'Attendance Trend (14 days)' : 'Trend Kehadiran (14 hari)'}
              </p>
              <p style={{ fontSize: '12px', color: C.gray500 }}>
                {lang === 'en' ? 'National average %' : 'Purata nasional %'}
              </p>
            </div>
            <span style={{ fontSize: '24px', fontWeight: 700, color: C.success }}>91.4%</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <svg width="100%" height="80" viewBox="0 0 400 80" preserveAspectRatio="none">
              {attendanceTrend.map((v, i) => {
                const x = (i / (attendanceTrend.length - 1)) * 380 + 10;
                const y = 70 - ((v - 85) / 10) * 60;
                return i === 0 ? null : (
                  <line
                    key={i}
                    x1={(( (i-1) / (attendanceTrend.length-1)) * 380 + 10)}
                    y1={70 - ((attendanceTrend[i-1]! - 85) / 10) * 60}
                    x2={x} y2={y}
                    stroke={C.yellow} strokeWidth="2.5" strokeLinecap="round"
                  />
                );
              })}
              {attendanceTrend.map((v, i) => (
                <circle
                  key={i}
                  cx={(i / (attendanceTrend.length - 1)) * 380 + 10}
                  cy={70 - ((v - 85) / 10) * 60}
                  r="3" fill={C.yellow}
                />
              ))}
            </svg>
          </div>
        </Card>

        {/* District breakdown */}
        <Card>
          <p style={{ fontSize: '14px', fontWeight: 700, color: C.gray900, marginBottom: '16px' }}>
            {lang === 'en' ? 'Students by District' : 'Pelajar Mengikut Daerah'}
          </p>
          {districtData.map((d) => (
            <MiniBar key={d.label} label={d.label} value={d.value} total={districtTotal} color={d.color} />
          ))}
        </Card>
      </div>

      <SectionHeader ms="Tindakan Diperlukan" en="Action Required" lang={lang} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
        {[
          { Icon: AlertCircle, color: C.error,   bgColor: C.errorBg,
            ms: '12 sekolah di bawah kehadiran 75%', en: '12 schools below 75% attendance' },
          { Icon: FileText,    color: C.warning,  bgColor: C.warningBg,
            ms: '34 permohonan belum diproses', en: '34 applications pending review' },
          { Icon: CheckCircle, color: C.success,  bgColor: C.successBg,
            ms: '156 laporan CPD melebihi 100 jam', en: '156 CPD records exceeded 100 hours' },
        ].map((item, i) => (
          <div
            key={i}
            style={{
              backgroundColor: item.bgColor,
              borderRadius: '10px',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              border: `1px solid ${item.color}30`,
            }}
          >
            <item.Icon size={20} color={item.color} strokeWidth={2} />
            <p style={{ margin: 0, fontSize: '13.5px', color: C.gray900, lineHeight: 1.4 }}>
              {lang === 'en' ? item.en : item.ms}
            </p>
          </div>
        ))}
      </div>
    </>
  );
}

// ── School Admin Dashboard ────────────────────────────────────────────────────

function SchoolAdminDashboard({ lang, navigate }: { lang: string; navigate: (p: string) => void }) {
  const stats: StatCardData[] = [
    { labelMs: 'Jumlah Pelajar',     labelEn: 'Total Students',      value: 842,  Icon: Users,        color: C.info },
    { labelMs: 'Hadir Hari Ini',     labelEn: 'Present Today',       value: 789,  trend: { dir: 'up', pct: '+1.2%' }, Icon: CalendarCheck, color: C.success },
    { labelMs: 'Permohonan Belum Selesai', labelEn: 'Pending Applications', value: 14, Icon: FileText, color: C.warning },
    { labelMs: 'Pendidik Aktif',     labelEn: 'Active Educators',    value: 68,   Icon: GraduationCap, color: C.yellow === C.yellow ? C.black : C.yellow },
  ];

  const quickActions: QuickAction[] = [
    { labelMs: 'Rekod Kehadiran', labelEn: 'Mark Attendance', descMs: 'Rekodkan kehadiran kelas', descEn: 'Record class attendance', path: '/admin/attendance', Icon: CalendarCheck, color: C.success },
    { labelMs: 'Pendaftaran Baru', labelEn: 'New Enrolment',  descMs: 'Proses permohonan pelajar', descEn: 'Process student application', path: '/admin/applications', Icon: FileText, color: C.info },
    { labelMs: 'Lihat Laporan',   labelEn: 'View Reports',   descMs: 'Jana laporan sekolah', descEn: 'Generate school reports', path: '/admin/reports', Icon: BarChart3, color: C.warning },
    { labelMs: 'Jadual Waktu',    labelEn: 'Timetable',      descMs: 'Urus jadual kelas', descEn: 'Manage class schedules', path: '/admin/timetable', Icon: Clock, color: '#8B5CF6' },
  ];

  const classAttendance = [
    { label: lang === 'en' ? 'Form 1' : 'Tingkatan 1', present: 142, total: 150 },
    { label: lang === 'en' ? 'Form 2' : 'Tingkatan 2', present: 136, total: 148 },
    { label: lang === 'en' ? 'Form 3' : 'Tingkatan 3', present: 158, total: 168 },
    { label: lang === 'en' ? 'Form 4' : 'Tingkatan 4', present: 172, total: 180 },
    { label: lang === 'en' ? 'Form 5' : 'Tingkatan 5', present: 181, total: 196 },
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {stats.map((s) => <StatCard key={s.labelEn} data={s} lang={lang} />)}
      </div>

      <SectionHeader ms="Tindakan Pantas" en="Quick Actions" lang={lang} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
        {quickActions.map((a) => (
          <QuickActionCard key={a.path} action={a} lang={lang} onClick={() => navigate(a.path)} />
        ))}
      </div>

      <SectionHeader ms="Kehadiran Hari Ini Mengikut Kelas" en="Today's Attendance by Class" lang={lang} />
      <Card>
        <p style={{ fontSize: '13px', color: C.gray500, marginBottom: '12px' }}>
          {lang === 'en' ? 'Showing 5 classes · Updated just now' : '5 kelas ditunjukkan · Dikemaskini baru sahaja'}
        </p>
        {classAttendance.map((row) => (
          <AttendanceRow key={row.label} label={row.label} present={row.present} total={row.total} />
        ))}
      </Card>
    </>
  );
}

// ── Teacher Dashboard ─────────────────────────────────────────────────────────

function TeacherDashboard({ lang, navigate }: { lang: string; navigate: (p: string) => void }) {
  const today = new Date();
  const dayNames = lang === 'en'
    ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
    : ['Ahad','Isn','Sel','Rab','Kha','Jum','Sab'];
  const dayName = dayNames[today.getDay()] ?? '';

  const myClasses = [
    { subject: lang === 'en' ? 'Mathematics' : 'Matematik',   form: lang === 'en' ? 'Form 4A' : 'T4A', time: '07:30', room: 'B12', done: false },
    { subject: lang === 'en' ? 'Science'     : 'Sains',       form: lang === 'en' ? 'Form 3B' : 'T3B', time: '09:00', room: 'Lab 2', done: true },
    { subject: lang === 'en' ? 'Mathematics' : 'Matematik',   form: lang === 'en' ? 'Form 5A' : 'T5A', time: '10:30', room: 'B14', done: false },
    { subject: lang === 'en' ? 'Add. Maths'  : 'Matematik Tambahan', form: lang === 'en' ? 'Form 5B' : 'T5B', time: '13:00', room: 'B15', done: false },
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {[
          { labelMs: 'Kelas Hari Ini', labelEn: 'Classes Today', value: 4, Icon: Clock, color: C.info },
          { labelMs: 'Ditanda Kehadiran', labelEn: 'Attendance Marked', value: 1, Icon: CalendarCheck, color: C.success },
          { labelMs: 'Gred Belum Dihantar', labelEn: 'Grades Pending', value: 12, Icon: BookOpen, color: C.warning },
          { labelMs: 'CPD Jam (Tahun Ini)', labelEn: 'CPD Hours (YTD)', value: 34, Icon: GraduationCap, color: '#8B5CF6' },
        ].map((s) => <StatCard key={s.labelEn} data={s} lang={lang} />)}
      </div>

      <SectionHeader ms={`Kelas ${dayName} Saya`} en={`My ${dayName} Classes`} lang={lang} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {myClasses.map((cls, i) => (
          <Card key={i} style={{ padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div
                style={{
                  width: '48px', height: '48px', borderRadius: '10px',
                  backgroundColor: cls.done ? C.successBg : C.yellowLight,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '13px',
                  color: cls.done ? C.successText : C.black,
                  flexShrink: 0,
                }}
              >
                {cls.time}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '14px', fontWeight: 700, color: C.gray900, marginBottom: '2px' }}>
                  {cls.subject} — {cls.form}
                </p>
                <p style={{ fontSize: '12px', color: C.gray500 }}>
                  {lang === 'en' ? `Room ${cls.room}` : `Bilik ${cls.room}`}
                </p>
              </div>
              {cls.done
                ? <span style={{ fontSize: '12px', fontWeight: 600, color: C.successText, backgroundColor: C.successBg, padding: '4px 12px', borderRadius: '999px' }}>
                    {lang === 'en' ? 'Done' : 'Selesai'}
                  </span>
                : <button
                    onClick={() => navigate('/admin/attendance')}
                    style={{
                      backgroundColor: C.yellow, color: C.black,
                      border: 'none', borderRadius: '8px',
                      padding: '8px 16px', fontSize: '13px', fontWeight: 600,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {lang === 'en' ? 'Mark Now' : 'Tandai Sekarang'}
                  </button>
              }
            </div>
          </Card>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        <button
          onClick={() => navigate('/admin/grades')}
          style={{
            backgroundColor: C.white, border: `1.5px solid ${C.border}`,
            borderRadius: '8px', padding: '10px 24px',
            fontSize: '14px', fontWeight: 600, color: C.gray700,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
          }}
        >
          <BookOpen size={16} strokeWidth={2} />
          {lang === 'en' ? 'Enter Grades' : 'Masukkan Gred'}
        </button>
      </div>
    </>
  );
}

// ── Parent Dashboard ──────────────────────────────────────────────────────────

function ParentDashboard({ lang, navigate }: { lang: string; navigate: (p: string) => void }) {
  const [selectedChild, setSelectedChild] = useState(0);

  const children = [
    { name: 'Ahmad Fahmi', form: lang === 'en' ? 'Form 4A' : 'Tingkatan 4A', school: 'SMAS Ali' },
    { name: 'Siti Nooraina', form: lang === 'en' ? 'Form 2B' : 'Tingkatan 2B', school: 'SMAS Ali' },
  ];

  const child = children[selectedChild]!;

  const attendancePct = 94;
  const recentGrades = [
    { subject: lang === 'en' ? 'Mathematics' : 'Matematik', grade: 'A', score: 88, trend: 'up' as const },
    { subject: lang === 'en' ? 'Science' : 'Sains', grade: 'B+', score: 76, trend: 'down' as const },
    { subject: lang === 'en' ? 'English' : 'Bahasa Inggeris', grade: 'A+', score: 95, trend: 'up' as const },
    { subject: lang === 'en' ? 'History' : 'Sejarah', grade: 'B', score: 71, trend: 'up' as const },
  ];

  return (
    <>
      {/* Child selector tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {children.map((c, i) => (
          <button
            key={i}
            onClick={() => setSelectedChild(i)}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 16px',
              backgroundColor: selectedChild === i ? C.yellow : C.white,
              color: selectedChild === i ? C.black : C.gray700,
              border: `1.5px solid ${selectedChild === i ? C.yellow : C.border}`,
              borderRadius: '999px',
              cursor: 'pointer', fontWeight: 600, fontSize: '13px',
              transition: 'all 0.15s',
            }}
          >
            <div
              style={{
                width: '28px', height: '28px', borderRadius: '50%',
                backgroundColor: selectedChild === i ? C.black : C.gray100,
                color: selectedChild === i ? C.white : C.gray700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700,
              }}
            >
              {c.name.slice(0, 2).toUpperCase()}
            </div>
            {c.name}
          </button>
        ))}
      </div>

      {/* School info */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '0' }}>
        <Card>
          <p style={{ fontSize: '12px', color: C.gray500, marginBottom: '8px', fontWeight: 500 }}>
            {lang === 'en' ? 'Class' : 'Kelas'}
          </p>
          <p style={{ fontSize: '20px', fontWeight: 700, color: C.gray900 }}>{child.form}</p>
          <p style={{ fontSize: '12px', color: C.gray500, marginTop: '4px' }}>{child.school}</p>
        </Card>

        <Card>
          <p style={{ fontSize: '12px', color: C.gray500, marginBottom: '8px', fontWeight: 500 }}>
            {lang === 'en' ? 'Today\'s Status' : 'Status Hari Ini'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                backgroundColor: C.successBg, color: C.successText,
                padding: '6px 16px', borderRadius: '999px',
                fontSize: '14px', fontWeight: 700,
              }}
            >
              ✓ {lang === 'en' ? 'Present' : 'Hadir'}
            </span>
          </div>
          <p style={{ fontSize: '12px', color: C.gray400, marginTop: '6px' }}>
            {lang === 'en' ? 'Checked in at 7:25 AM' : 'Daftar masuk pukul 7:25 PG'}
          </p>
        </Card>

        <Card>
          <p style={{ fontSize: '12px', color: C.gray500, marginBottom: '8px', fontWeight: 500 }}>
            {lang === 'en' ? 'Attendance Rate' : 'Kadar Kehadiran'}
          </p>
          <p style={{ fontSize: '32px', fontWeight: 700, color: C.success, lineHeight: 1 }}>{attendancePct}%</p>
          <div style={{ height: '6px', backgroundColor: C.gray100, borderRadius: '999px', marginTop: '8px' }}>
            <div style={{ height: '100%', width: `${attendancePct}%`, backgroundColor: C.success, borderRadius: '999px' }} />
          </div>
        </Card>

        <Card>
          <p style={{ fontSize: '12px', color: C.gray500, marginBottom: '8px', fontWeight: 500 }}>
            {lang === 'en' ? 'Unread Notifications' : 'Notifikasi Belum Dibaca'}
          </p>
          <p style={{ fontSize: '32px', fontWeight: 700, color: C.info, lineHeight: 1 }}>3</p>
          <button
            onClick={() => navigate('/admin/attendance/alerts')}
            style={{ fontSize: '12px', color: C.info, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontWeight: 600 }}
          >
            {lang === 'en' ? 'View all →' : 'Lihat semua →'}
          </button>
        </Card>
      </div>

      <SectionHeader ms="Gred Terkini" en="Recent Grades" lang={lang} />
      <Card style={{ padding: '0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: C.gray50, borderBottom: `1px solid ${C.border}` }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: C.gray500, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {lang === 'en' ? 'Subject' : 'Mata Pelajaran'}
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: C.gray500, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {lang === 'en' ? 'Grade' : 'Gred'}
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: C.gray500, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {lang === 'en' ? 'Score' : 'Markah'}
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: C.gray500, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {lang === 'en' ? 'Trend' : 'Trend'}
              </th>
            </tr>
          </thead>
          <tbody>
            {recentGrades.map((g, i) => (
              <tr key={i} style={{ borderBottom: i < recentGrades.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <td style={{ padding: '14px 16px', color: C.gray900, fontWeight: 500 }}>{g.subject}</td>
                <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                  <span
                    style={{
                      backgroundColor: g.grade.startsWith('A') ? C.successBg : g.grade.startsWith('B') ? C.yellowLight : C.errorBg,
                      color: g.grade.startsWith('A') ? C.successText : g.grade.startsWith('B') ? '#7A5A00' : C.errorText,
                      padding: '3px 12px', borderRadius: '999px',
                      fontSize: '12px', fontWeight: 700,
                    }}
                  >
                    {g.grade}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 600, color: C.gray900 }}>
                  {g.score}
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                  {g.trend === 'up'
                    ? <TrendingUp size={16} color={C.success} />
                    : <TrendingDown size={16} color={C.error} />
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function DashboardPage({ user }: { user: AuthUser }) {
  const { lang } = useLang();
  const navigate  = useNavigate();

  const isSuperAdmin = user.roles.some((r) => r.role === 'super_admin');
  const isAdmin      = user.roles.some((r) => r.role === 'admin');
  const isTeacher    = user.roles.some((r) => r.role === 'teacher');
  const isParent     = user.roles.some((r) => r.role === 'parent');

  const title = lang === 'en' ? 'Dashboard' : 'Papan Pemuka';
  usePageTitle(`${title}`);

  // Welcome line
  const greeting = (() => {
    const h = new Date().getHours();
    if (lang === 'en') return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
    return h < 12 ? 'Selamat pagi' : h < 17 ? 'Selamat tengah hari' : 'Selamat petang';
  })();

  const displayName = user.email.split('@')[0] ?? user.email;

  return (
    <>
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>

      {/* Welcome header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '4px', fontFamily: '"Noto Sans", sans-serif' }}>
          {greeting}, {displayName} 👋
        </h1>
        <p style={{ fontSize: '14px', color: '#6B7280' }}>
          {new Date().toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            timeZone: 'Asia/Brunei',
          })}
        </p>
      </div>

      {/* Role-based dashboard */}
      {(isSuperAdmin) && <MOEDashboard lang={lang} />}
      {(isAdmin && !isSuperAdmin) && <SchoolAdminDashboard lang={lang} navigate={navigate} />}
      {(isTeacher && !isAdmin && !isSuperAdmin) && <TeacherDashboard lang={lang} navigate={navigate} />}
      {(isParent && !isTeacher && !isAdmin && !isSuperAdmin) && <ParentDashboard lang={lang} navigate={navigate} />}
      {(!isSuperAdmin && !isAdmin && !isTeacher && !isParent) && (
        <Card>
          <p style={{ textAlign: 'center', color: '#6B7280', padding: '40px' }}>
            {lang === 'en' ? 'Welcome to SERPS. Your dashboard is being configured.' : 'Selamat datang ke SERPS. Papan pemuka anda sedang dikonfigurasi.'}
          </p>
        </Card>
      )}

      {/* EduBot CTA */}
      <div style={{ marginTop: '32px' }}>
        <Card
          onClick={() => navigate('/admin/edubot')}
          style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)', border: 'none', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div
              style={{
                width: '48px', height: '48px', borderRadius: '12px',
                backgroundColor: '#F7D000',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Bot size={24} color="#1A1A1A" strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '15px', fontWeight: 700, color: '#FFFFFF', marginBottom: '4px' }}>
                EduBot — {lang === 'en' ? 'AI Assistant' : 'Pembantu AI'}
              </p>
              <p style={{ fontSize: '13px', color: '#9CA3AF' }}>
                {lang === 'en'
                  ? 'Ask anything about student records, attendance, or reports'
                  : 'Tanya apa sahaja tentang rekod pelajar, kehadiran, atau laporan'}
              </p>
            </div>
            <span style={{ color: '#F7D000', fontSize: '20px', flexShrink: 0 }}>→</span>
          </div>
        </Card>
      </div>
    </>
  );
}
